import { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { C } from '../../lib/tokens.js';
import { useClickOutside } from '../../lib/useClickOutside.js';
import { useViewport } from '../../lib/useViewport.js';

export function Field({ label, value, onChange, required, disabled, error, helper, placeholder, type = 'text', icon, autoFocus, style: extra, density = 'comfortable', maxLength, step }) {
  const [focus, setFocus] = useState(false);
  const borderColor = disabled ? 'transparent' : error ? C.rust : focus ? C.liquid : C.mercury;
  const padY = density === 'compact' ? 5 : 8;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...extra }}>
      {label ? (
        <label style={{ fontSize: 12, fontWeight: 500, color: C.iron }}>
          {label}{required ? <span style={{ color: C.copper }}> *</span> : null}
        </label>
      ) : null}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: `${padY}px 10px`, borderRadius: 3,
        border: `1px solid ${borderColor}`,
        background: disabled ? C.platinum : C.tin,
        boxShadow: focus && !disabled ? '0 0 0 2px rgba(37,168,224,0.18)' : 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
      }}>
        {icon ? <span className="material-icons" style={{ fontSize: 16, color: C.iron }}>{icon}</span> : null}
        <input
          autoFocus={autoFocus} type={type} maxLength={maxLength} step={step}
          value={value || ''} onChange={e => onChange?.(e.target.value)}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          disabled={disabled} placeholder={placeholder}
          style={{
            fontFamily: 'Roboto, sans-serif', fontSize: 13, border: 'none', outline: 'none',
            background: 'transparent', color: disabled ? C.silver : C.fg, flex: 1, minWidth: 0,
          }}
        />
      </div>
      {error ? <div style={{ fontSize: 11, color: C.rust }}>{error}</div>
        : helper ? <div style={{ fontSize: 11, color: C.iron }}>{helper}</div>
        : null}
    </div>
  );
}

export function Textarea({ label, value, onChange, required, placeholder, rows = 4, helper, autoFocus }) {
  const [focus, setFocus] = useState(false);
  const borderColor = focus ? C.liquid : C.mercury;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label ? (
        <label style={{ fontSize: 12, fontWeight: 500, color: C.iron }}>
          {label}{required ? <span style={{ color: C.copper }}> *</span> : null}
        </label>
      ) : null}
      <textarea
        autoFocus={autoFocus} value={value || ''}
        onChange={e => onChange?.(e.target.value)}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        placeholder={placeholder} rows={rows}
        style={{
          fontFamily: 'Roboto, sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 3,
          border: `1px solid ${borderColor}`, background: C.tin, color: C.fg, outline: 'none',
          resize: 'vertical', boxShadow: focus ? '0 0 0 2px rgba(37,168,224,0.18)' : 'none',
          transition: 'border-color 120ms, box-shadow 120ms', lineHeight: 1.5,
        }}
      />
      {helper ? <div style={{ fontSize: 11, color: C.iron }}>{helper}</div> : null}
    </div>
  );
}

export function Select({ label, value, onChange, options, required, helper, placeholder = 'Select…' }) {
  const [focus, setFocus] = useState(false);
  const borderColor = focus ? C.liquid : C.mercury;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label ? (
        <label style={{ fontSize: 12, fontWeight: 500, color: C.iron }}>
          {label}{required ? <span style={{ color: C.copper }}> *</span> : null}
        </label>
      ) : null}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: 3,
        border: `1px solid ${borderColor}`, background: C.tin, overflow: 'hidden',
        boxShadow: focus ? '0 0 0 2px rgba(37,168,224,0.18)' : 'none', position: 'relative',
      }}>
        <select
          value={value || ''} onChange={e => onChange?.(e.target.value)}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          style={{
            fontFamily: 'Roboto, sans-serif', fontSize: 13, padding: '8px 0',
            border: 'none', outline: 'none', background: 'transparent', flex: 1, minWidth: 0, color: C.fg,
            appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
            textOverflow: 'ellipsis',
          }}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map(o => (
            <option key={o.id || o.value || o} value={o.id || o.value || o}>{o.name ?? o.label ?? o}</option>
          ))}
        </select>
        <span className="material-icons" style={{ fontSize: 18, color: C.iron, pointerEvents: 'none' }}>expand_more</span>
      </div>
      {helper ? <div style={{ fontSize: 11, color: C.iron }}>{helper}</div> : null}
    </div>
  );
}

// SearchableSelect menu geometry (px): gap to the trigger, margin from the viewport edge,
// height the menu would like (search box + list), search box height, smallest list height.
const MENU_GAP = 4;
const MENU_MARGIN = 8;
const MENU_PREFERRED_HEIGHT = 290;
const MENU_SEARCH_HEIGHT = 50;
const MENU_MIN_LIST_HEIGHT = 80;

export function SearchableSelect({ label, value, onChange, options, required, helper, placeholder = 'Select…' }) {
  const { isMobile } = useViewport();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  // The dropdown panel below is portaled to document.body (see the createPortal
  // call) so it escapes any scrollable ancestor (e.g. an edit modal's own
  // overflow-y: auto content) instead of growing that ancestor's scroll height -
  // a position:absolute child still contributes to its nearest overflow ancestor's
  // scrollHeight even though it's visually "floating". menuRef/menuRect track the
  // portaled panel for the click-outside check and its viewport position.
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  useClickOutside(ref, open, () => { setOpen(false); setQuery(''); }, menuRef);

  useLayoutEffect(() => {
    if (!open) return;
    function updateRect() {
      if (!triggerRef.current) return;
      // Anchored to the button, not the wrapper (which also holds label + helper). Opens
      // upward when there's more room above - a fixed menu running off the bottom of the
      // viewport (e.g. ChatIntake's bottom panel) can't be scrolled into view.
      const r = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom - MENU_GAP - MENU_MARGIN;
      const spaceAbove = r.top - MENU_GAP - MENU_MARGIN;
      const openUp = spaceBelow < MENU_PREFERRED_HEIGHT && spaceAbove > spaceBelow;
      setMenuRect({
        top: openUp ? undefined : r.bottom + MENU_GAP,
        bottom: openUp ? window.innerHeight - r.top + MENU_GAP : undefined,
        left: r.left, width: r.width,
        listMaxHeight: Math.max(MENU_MIN_LIST_HEIGHT, (openUp ? spaceAbove : spaceBelow) - MENU_SEARCH_HEIGHT),
      });
    }
    updateRect();
    // Capture phase so a scroll on ANY ancestor (not just window) repositions this -
    // notably the modal card itself, which is what this fix is mainly for.
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [open]);

  const cur = options.find(o => (o.id ?? o.value) === value);
  const filtered = query.trim()
    ? options.filter(o => (o.name ?? o.label ?? '').toLowerCase().includes(query.trim().toLowerCase()))
    : options;
  const itemPad = isMobile ? '11px 12px' : '7px 10px';
  const itemFontSize = isMobile ? 14 : 12;

  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
      {label ? (
        <label style={{ fontSize: 12, fontWeight: 500, color: C.iron }}>
          {label}{required ? <span style={{ color: C.copper }}> *</span> : null}
        </label>
      ) : null}

      <button ref={triggerRef} type="button" onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', width: '100%', boxSizing: 'border-box',
          padding: isMobile ? '11px 12px' : '8px 10px', borderRadius: isMobile ? 6 : 3,
          border: `1px solid ${open ? C.liquid : C.mercury}`,
          background: C.tin, boxShadow: open ? '0 0 0 2px rgba(37,168,224,0.18)' : 'none',
          cursor: 'pointer', fontFamily: 'Roboto, sans-serif', fontSize: isMobile ? 14 : 13, textAlign: 'left',
        }}>
        <span style={{ flex: 1, color: cur ? C.fg : C.silver, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cur ? (cur.name ?? cur.label) : placeholder}
        </span>
        <span className="material-icons" style={{ fontSize: 18, color: C.iron }}>expand_more</span>
      </button>

      {open && menuRect && createPortal(
        <div ref={menuRef} style={{
          position: 'fixed', top: menuRect.top, bottom: menuRect.bottom, left: menuRect.left, width: menuRect.width, zIndex: 300,
          background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: isMobile ? 8 : 4,
          boxShadow: '0 6px 16px rgba(34,52,105,0.14)', padding: 4,
        }}>
          <input
            autoFocus value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Type to search…"
            style={{
              width: '100%', boxSizing: 'border-box', marginBottom: 4,
              padding: isMobile ? '9px 10px' : '6px 8px',
              border: `1px solid ${C.mercury}`, borderRadius: 3, fontSize: isMobile ? 14 : 12,
              fontFamily: 'Roboto', outline: 'none',
            }}
          />
          <div style={{ maxHeight: Math.min(isMobile ? 280 : 240, menuRect.listMaxHeight), overflowY: 'auto', overscrollBehavior: 'contain' }}>
            {!required && (
              <button type="button" onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                style={{
                  width: '100%', textAlign: 'left', padding: itemPad, border: 'none',
                  background: !value ? C.rowActive : 'transparent',
                  color: !value ? C.deepsea : C.silver, fontSize: itemFontSize, fontStyle: 'italic',
                  cursor: 'pointer', borderRadius: 3, fontFamily: 'Roboto',
                }}>
                {placeholder}
              </button>
            )}
            {filtered.length === 0 ? (
              <div style={{ padding: itemPad, fontSize: itemFontSize, color: C.iron, fontStyle: 'italic' }}>No matches</div>
            ) : filtered.map(o => {
              const id = o.id ?? o.value;
              const active = id === value;
              return (
                <button key={id} type="button" onClick={() => { onChange(id); setOpen(false); setQuery(''); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: itemPad, border: 'none',
                    background: active ? C.rowActive : 'transparent',
                    color: active ? C.deepsea : C.fg, fontSize: itemFontSize, cursor: 'pointer', borderRadius: 3, fontFamily: 'Roboto',
                  }}>
                  {o.name ?? o.label}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {helper ? <div style={{ fontSize: 11, color: C.iron }}>{helper}</div> : null}
    </div>
  );
}
